import { Box, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHits, useInstantSearch } from 'react-instantsearch';
import { AlgoliaHit } from 'instantsearch.js';
import { useMemo } from 'react';
import { ProductHit } from '../../search/search';
import i18n from '../../i18n';
import EmptyPagePlaceholder from '../../common/components/EmptyPagePlaceholder';
import TrumpeterButterflyImage from '../../assets/images/trumpeter_butterfly.png';
import DiscoveryViewSwitch from '../DiscoveryViewSwitch';
import QueryTag from '../../search/filters/QueryTag';
import GeoLocationTags from '../../search/filters/GeoLocationTags';
import ProductLabelsTags from '../filters/ProductLabelsTags';
import ClearFiltersButton from '../../search/filters/ClearFiltersButton';
import DiscoverySkeleton from '../DiscoverySkeleton';
import ProductHitCard from '../../products/common/ProductHitCard';
import { mapAlgoliaHitToProductCardData } from '../../products/common/product-card.mappers';
import { FeatureFlagKey, isFeatureEnabled } from '../../common/utils/featureFlags';
import DiscoveryClassificationHeading from '../navigation/DiscoveryClassificationHeading';
import DiscoveryFilters from './DiscoveryFilters';
import SocialTagFilterTags from './filters/SocialTagFilterTags';
import ClassificationTagFilterTags from './filters/ClassificationTagFilterTags';
import { CLASSIFICATION_TAG_SHEET_FILTERS } from './filters/classificationTagFilters';

export default function DiscoveryProductHits(): React.JSX.Element {
  const { t } = useTranslation(['discovery', 'products', 'product-classification-tags']);
  const tagItemLabel = (value: string): string => t(`product-classification-tags:${value}`);
  const { items, results } = useHits<AlgoliaHit<ProductHit>>();
  const { status } = useInstantSearch();
  const format = new Intl.NumberFormat(i18n.resolvedLanguage);
  const classificationEnabled = isFeatureEnabled(FeatureFlagKey.PRODUCT_CLASSIFICATION);
  const resultsLabel = (
    <Typography variant="bodyMedium" sx={{ color: 'onSurfaceVariant.main' }}>
      {t('discovery:N_product', { count: results!.nbHits, formattedCount: format.format(results!.nbHits) })}
    </Typography>
  );

  const mainComponent = useMemo(() => {
    if (status === 'loading' || !results?.processingTimeMS) {
      return <DiscoverySkeleton />;
    }

    return items.length
      ? (
        <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 1.5 }}>
          {items.map((hit) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2.4 }} key={hit.objectID}>
              <ProductHitCard displayCompany displaySuggestedRetailPrice displayWishlistButton product={mapAlgoliaHitToProductCardData(hit)} />
            </Grid>
          ))}
        </Grid>
      )
      : (
        <EmptyPagePlaceholder
          imageSrc={TrumpeterButterflyImage}
          title={t('products:no_product')}
        />
      );
  }, [items, status, results]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: {
          xs: 2,
          sm: 0,
        },
      }}
    >
      <>
        <Box
          sx={{
            position: 'sticky',
            top: 80,
            zIndex: 7,
            pt: 1,
            backgroundColor: 'surface.main',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column-reverse',
                lg: 'row',
              },
              alignItems: 'center',
              gap: {
                xs: 1.5,
                lg: 2,
              },
              mb: 1,
            }}
          >
            <DiscoveryFilters
              fullTextSearchPlaceholder={t('discovery:search_a_product')}
              index={import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX}
              resultsLabel={classificationEnabled ? resultsLabel : undefined}
            />
            {!classificationEnabled && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: {
                    lg: 'end',
                    xs: 'space-between',
                  },
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: {
                      lg: 'none',
                    },
                  }}
                >
                  <DiscoveryViewSwitch />
                </Box>
                {resultsLabel}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              '&:not(:empty)': {
                py: 1,
              },
            }}
          >
            <QueryTag />
            <SocialTagFilterTags attribute="social_tags_lvl1" />
            <SocialTagFilterTags attribute="social_tags_lvl2" />
            {classificationEnabled && CLASSIFICATION_TAG_SHEET_FILTERS.map(({ attribute }) => (
              <ClassificationTagFilterTags key={attribute} attribute={attribute} getItemLabel={tagItemLabel} />
            ))}
            <GeoLocationTags />
            <ProductLabelsTags />
            <ClearFiltersButton
              sx={{ height: 36 }}
              attributesToCheck={['administrative_areas', 'labels', 'social_tags_lvl1', 'social_tags_lvl2', ...CLASSIFICATION_TAG_SHEET_FILTERS.map(({ attribute }) => attribute)]}
              excludedAttributes={['family']}
              searchMode="PRODUCT"
            />
          </Box>
        </Box>
        <DiscoveryClassificationHeading />
        {mainComponent}
      </>
    </Box>
  );
}